/**
 * Regras do carrinho do PDV, separadas da tela.
 *
 * Existem fora do componente por dois motivos: a trava de estoque precisa
 * valer igualmente para a leitura por câmera e para os botões +/-, e é a única
 * forma de testar essas regras sem montar a árvore React.
 *
 * O servidor continua sendo a autoridade sobre estoque (`vendas.create`
 * revalida). A trava daqui existe para o caixa descobrir o problema ao
 * escanear, e não no fim, com o cliente esperando.
 */

/** Uma peça física no carrinho. */
export interface UnidadeNoCarrinho {
  id: string;
  codigo: string;
}

export interface ItemCarrinho {
  produtoId: string;
  codigo: string;
  nome: string;
  preco: number;
  /**
   * As peças específicas que vão sair nesta venda. A quantidade do item é o
   * tamanho desta lista — é o que impede a mesma etiqueta de entrar duas vezes.
   */
  unidades: UnidadeNoCarrinho[];
  /** Estoque conhecido na última vez que o produto foi consultado. */
  estoqueDisponivel: number;
}

export interface ProdutoParaCarrinho {
  id: string;
  codigo: string;
  nome: string;
  preco: string;
  quantidadeEstoque: number;
  ativo: boolean;
  unidade: UnidadeNoCarrinho;
}

/** Ids de todas as peças no carrinho — o servidor usa para não repetir peça. */
export function unidadesNoCarrinho(carrinho: ItemCarrinho[]): string[] {
  return carrinho.flatMap((item) => item.unidades.map((u) => u.id));
}

export const quantidadeDe = (item: ItemCarrinho) => item.unidades.length;

export type ResultadoCarrinho =
  | { ok: true; carrinho: ItemCarrinho[]; item: ItemCarrinho }
  | { ok: false; motivo: string };

export function adicionarItem(
  carrinho: ItemCarrinho[],
  produto: ProdutoParaCarrinho
): ResultadoCarrinho {
  if (!produto.ativo) {
    return { ok: false, motivo: `${produto.nome} está inativo e não pode ser vendido` };
  }

  if (produto.quantidadeEstoque <= 0) {
    return { ok: false, motivo: `${produto.nome} está sem estoque` };
  }

  const existente = carrinho.find((item) => item.produtoId === produto.id);
  const jaNoCarrinho = existente?.unidades.length ?? 0;

  // A mesma etiqueta lida duas vezes é a peça que já está no carrinho, não uma
  // segunda peça. É exatamente o que os códigos por peça vieram resolver.
  if (existente?.unidades.some((u) => u.id === produto.unidade.id)) {
    return { ok: false, motivo: `A peça ${produto.unidade.codigo} já está no carrinho` };
  }

  if (jaNoCarrinho >= produto.quantidadeEstoque) {
    return {
      ok: false,
      motivo: `Estoque insuficiente para ${produto.nome}. Disponível: ${produto.quantidadeEstoque}`,
    };
  }

  const atualizado: ItemCarrinho = {
    produtoId: produto.id,
    codigo: produto.codigo,
    nome: produto.nome,
    preco: Number.parseFloat(produto.preco),
    unidades: [...(existente?.unidades ?? []), produto.unidade],
    // Cada leitura traz o estoque fresco: outro caixa pode ter vendido nesse
    // meio tempo.
    estoqueDisponivel: produto.quantidadeEstoque,
  };

  return {
    ok: true,
    item: atualizado,
    carrinho: existente
      ? carrinho.map((item) => (item.produtoId === produto.id ? atualizado : item))
      : [...carrinho, atualizado],
  };
}

/**
 * Só sabe tirar peça, não pôr.
 *
 * Aumentar exigiria escolher qual peça entra, e quem escolhe é o servidor
 * (pela leitura ou pelo toque no produto). O botão de mais no carrinho passa
 * pelo mesmo caminho de adicionar.
 */
export function removerUnidade(carrinho: ItemCarrinho[], produtoId: string): ItemCarrinho[] {
  return carrinho
    .map((item) =>
      item.produtoId === produtoId ? { ...item, unidades: item.unidades.slice(0, -1) } : item
    )
    .filter((item) => item.unidades.length > 0);
}

export function removerItem(carrinho: ItemCarrinho[], produtoId: string): ItemCarrinho[] {
  return carrinho.filter((item) => item.produtoId !== produtoId);
}

export function calcularTotal(carrinho: ItemCarrinho[]): number {
  return carrinho.reduce((soma, item) => soma + item.preco * item.unidades.length, 0);
}
